import React, { useState } from 'react';
import {
  Box, Button, ButtonProps, IconButton, Paper, Snackbar, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

const KEY_DISMISSED = 'pwa_install_dismissed';
const KEY_IOS_SHOWN = 'pwa_ios_prompt_shown';

// ─── iOS instruction overlay ──────────────────────────────────────────────────
export function IOSOverlay({ onDismiss }: { onDismiss: () => void }) {
  return (
    <Box
      role="dialog"
      aria-label="Install Percoro"
      sx={{
        position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
        width: 'calc(100% - 32px)', maxWidth: 380,
        bgcolor: 'background.paper', borderRadius: 2, boxShadow: 12,
        p: 2, zIndex: 1400, display: 'flex', flexDirection: 'column', gap: 1.5,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={700}>Install Percoro</Typography>
        <IconButton size="small" onClick={onDismiss} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="body2" sx={{ color: '#1A2826', fontWeight: 500 }}>
        Add Percoro to your home screen — no App Store needed.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'grey.100', borderRadius: 1.5, px: 1.5, py: 1 }}>
        <IosShareIcon sx={{ color: '#007AFF', fontSize: 22, flexShrink: 0 }} />
        <Typography variant="body2">
          <span style={{color:"#1A2826"}}>Tap <strong>Share</strong> then <strong>Add to Home Screen</strong></span>
        </Typography>
      </Box>
      <Box aria-hidden sx={{
        position: 'absolute', bottom: -9, left: '50%', transform: 'translateX(-50%)',
        width: 0, height: 0,
        borderLeft: '9px solid transparent', borderRight: '9px solid transparent',
        borderTop: '9px solid', borderTopColor: 'background.paper',
      }} />
    </Box>
  );
}

// ─── Standalone install button — place anywhere on a page ─────────────────────
export function PwaInstallButton(props: Omit<ButtonProps, 'onClick'>) {
  const { canInstall, isIOS, install } = usePwaInstall();
  const [showIOS, setShowIOS] = useState(false);

  if (!canInstall) return null;

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<GetAppIcon />}
        onClick={isIOS ? () => setShowIOS(true) : install}
        {...props}
      >
        Install App
      </Button>
      {showIOS && <IOSOverlay onDismiss={() => setShowIOS(false)} />}
    </>
  );
}

// ─── Floating banner — auto-shows when install is available ───────────────────
export function PwaInstallPrompt() {
  const { canInstall, isIOS, install } = usePwaInstall();
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const pwaInstallEnabled = !flagsLoading && isEnabled('pwa_install') === true;
  // Gate: never render the install prompt unless the 'pwa_install' flag is ON.
  const [dismissed, setDismissed] = useState(localStorage.getItem(KEY_DISMISSED) === 'true');
  const [showIOS, setShowIOS] = useState(
    isIOS && localStorage.getItem(KEY_IOS_SHOWN) !== 'true'
  );

  const handleDismiss = () => {
    localStorage.setItem(KEY_DISMISSED, 'true');
    localStorage.setItem(KEY_IOS_SHOWN, 'true');
    setDismissed(true);
    setShowIOS(false);
  };

  const handleInstall = async () => {
    await install();
    handleDismiss();
  };

  if (!pwaInstallEnabled) return null;

  // iOS: show the overlay automatically (no beforeinstallprompt on iOS)
  if (showIOS && !dismissed) {
    return <IOSOverlay onDismiss={handleDismiss} />;
  }

  // Android / Desktop: show floating banner as soon as prompt is available
  if (!canInstall || dismissed || isIOS) return null;

  return (
    <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ mb: { xs: 9, sm: 2 } }}>
      <Paper elevation={6} sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1.5, borderRadius: 2, maxWidth: 380, width: '100%',
      }}>
        <GetAppIcon color="primary" sx={{ flexShrink: 0 }} />
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" fontWeight={700} noWrap>Install Percoro</Typography>
          <Typography variant="caption" color="text.secondary">
            Add to your home screen for the best experience
          </Typography>
        </Box>
        <Button size="small" variant="contained" color="primary" onClick={handleInstall} sx={{ flexShrink: 0 }}>
          Install
        </Button>
        <IconButton size="small" onClick={handleDismiss} aria-label="Dismiss">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
}